import { DEFAULT_VALUES, RUNTIME_DATA_EXAMPLE } from '../constants';
import i18n from 'i18next'; // This is the i18n instance we created

// Track the current language
let currentLanguage = i18n.language || 'en-US';

// Update the language whenever it changes
i18n.on('languageChanged', (lng) => {
  console.log('Language changed to:', lng);
  currentLanguage = lng;
});
class ApiService {
  constructor() {
    this.baseUrl = DEFAULT_VALUES.SERVER_URL;
    this.voiceServerUrl = DEFAULT_VALUES.VOICE_SERVER_URL;
  }

  // Authentication
  async login(email, password) {
    try {
      console.log('Logging in with email:', email);

      const response = await fetch(`${this.baseUrl}/api/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': navigator.userAgent || 'InterviewPOC/1.0'
        },
        credentials: 'include', // Important for cookies
        body: JSON.stringify({
          email,
          password
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.detail || 'Login failed');
      }

      const data = await response.json();

      // Handle nested response structure from unified-backend
      // Response structure: { status, message, data: { message, user: {...} } }
      const userData = data.data?.user || data.user;

      if (!userData) {
        throw new Error('Invalid response format from server');
      }

      // Return user data with tokens
      return {
        user: userData,
        accessToken: userData.accessToken,
        sessionId: userData.sessionId,
        message: data.data?.message || data.message
      };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async register(email, password, firstName, lastName) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': navigator.userAgent || 'InterviewPOC/1.0'
        },
        credentials: 'include',
        body: JSON.stringify({
          email,
          password,
          firstName,
          lastName
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.detail || 'Registration failed');
      }

      const data = await response.json();

      // Handle nested response structure from unified-backend
      return data.data || data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  async getAuthStatus(apiKey) {
    try {
      const response = await fetch(`${this.baseUrl}/auth/status`, {
        method: 'GET',
        headers: {
          'X-API-Key': apiKey
        }
      });

      if (!response.ok) {
        throw new Error('Status check failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Status check error:', error);
      throw error;
    }
  }

  // Runtime Data
  async sendRuntimeData(sessionId, runtimeData = null) {
    console.log(`🚀 ATTEMPTING TO SEND RUNTIME DATA for session: ${sessionId}`);
    try {
      const data = runtimeData || RUNTIME_DATA_EXAMPLE;
      
      const response = await fetch(`${this.baseUrl}/api/runtime/data/${sessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Runtime data send failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Runtime data error:', error);
      throw error;
    }
  }

  // Resume Management (for voice bot - old backend)
  async uploadResume(userIdentity, file, apiKey) {
    try {
      const formData = new FormData();
      formData.append('user_id', userIdentity);
      formData.append('file', file);

      // Upload to voice server (old backend) for voice bot to access
      const response = await fetch(`${this.voiceServerUrl}/api/resume/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Resume upload failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Resume upload error:', error);
      throw error;
    }
  }

  async listResumes(userIdentity, apiKey) {
    try {
      const response = await fetch(`${this.baseUrl}/api/resume/list/${userIdentity}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to fetch resumes');
      }

      return await response.json();
    } catch (error) {
      console.error('List resumes error:', error);
      throw error;
    }
  }

  async generateQuestions(resumeId, userIdentity, apiKey, options = {}) {
    try {
      const requestBody = {
        resume_id: resumeId,
        user_id: userIdentity,
        question_count: options.questionCount || 5,
        difficulty_level: options.difficultyLevel || 'medium',
        categories: options.categories || ['technical', 'behavioral']
      };

      const response = await fetch(`${this.baseUrl}/api/resume/generate-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Question generation failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Question generation error:', error);
      throw error;
    }
  }

  // Job Description Management (for voice bot - old backend)
  async uploadJDText(userIdentity, jobTitle, companyName, jdContent, apiKey) {
    try {
      const formData = new FormData();
      formData.append('user_id', userIdentity);
      formData.append('job_title', jobTitle);
      formData.append('company_name', companyName || '');
      formData.append('jd_content', jdContent);

      // Upload to voice server (old backend) for voice bot to access
      const response = await fetch(`${this.voiceServerUrl}/api/jd/upload-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'JD upload failed');
      }

      return await response.json();
    } catch (error) {
      console.error('JD text upload error:', error);
      throw error;
    }
  }

  async uploadJDFile(userIdentity, jobTitle, companyName, file, apiKey) {
    try {
      const formData = new FormData();
      formData.append('user_id', userIdentity);
      formData.append('job_title', jobTitle);
      formData.append('company_name', companyName || '');
      formData.append('file', file);

      // Upload to voice server (old backend) for voice bot to access
      const response = await fetch(`${this.voiceServerUrl}/api/jd/upload-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'JD upload failed');
      }

      return await response.json();
    } catch (error) {
      console.error('JD file upload error:', error);
      throw error;
    }
  }

  async listJDs(userIdentity, apiKey) {
    try {
      const response = await fetch(`${this.baseUrl}/api/jd/list/${userIdentity}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to fetch JDs');
      }

      return await response.json();
    } catch (error) {
      console.error('List JDs error:', error);
      throw error;
    }
  }

  async getJD(jdId, apiKey) {
    try {
      const response = await fetch(`${this.baseUrl}/api/jd/${jdId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to fetch JD');
      }

      return await response.json();
    } catch (error) {
      console.error('Get JD error:', error);
      throw error;
    }
  }

  async deleteJD(jdId, apiKey) {
    try {
      const response = await fetch(`${this.baseUrl}/api/jd/${jdId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete JD');
      }

      return await response.json();
    } catch (error) {
      console.error('Delete JD error:', error);
      throw error;
    }
  }

  // Interview Management (Unified Backend)
  async createInterview(resumeId, jobRoleId, jwtToken, sessionToken, options = {}) {
    try {
      // Check if a resume file is provided
      const resumeFile = options.resumeFile;

      if (resumeFile) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append('resumeFile', resumeFile);

        // Create a JSON string for the other fields to preserve types
        const interviewData = {
          jobRoleId,
          numberOfQuestions: options.numberOfQuestions || 5,
          language: currentLanguage,
          difficulty: options.difficulty || 'medium',
          durationMinutes: options.durationMinutes || 15,
          resumable: options.resumable || false
        };

        // Include baselineTestId if provided
        if (options.baselineTestId) {
          interviewData.baselineTestId = options.baselineTestId;
        }

        // Append each field individually (FormData will convert to strings, but backend DTO will transform)
        Object.keys(interviewData).forEach(key => {
          formData.append(key, interviewData[key]);
        });

        // Don't set Content-Type header - browser will set it with boundary for multipart/form-data
        const response = await fetch(`${this.baseUrl}/api/v1/interviews`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${jwtToken}`,
            'x-session-token': sessionToken
          },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Interview creation failed');
        }

        return await response.json();
      } else {
        // Use JSON for regular request without file
        const requestBody = {
          jobRoleId,
          numberOfQuestions: options.numberOfQuestions || 5,
          language: currentLanguage,
          difficulty: options.difficulty || 'medium',
          durationMinutes: options.durationMinutes || 15,
          resumable: options.resumable || false
        };

        // Only include resumeId if it's provided (not null/undefined/empty)
        if (resumeId) {
          requestBody.resumeId = resumeId;
        }

        // Include baselineTestId if provided (for baseline assessments)
        if (options.baselineTestId) {
          requestBody.baselineTestId = options.baselineTestId;
        }

        const response = await fetch(`${this.baseUrl}/api/v1/interviews`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${jwtToken}`,
            'x-session-token': sessionToken
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Interview creation failed');
        }

        return await response.json();
      }
    } catch (error) {
      console.error('Interview creation error:', error);
      throw error;
    }
  }

  async getInterview(sessionId, jwtToken, sessionToken) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/interviews/${sessionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch interview');
      }

      return await response.json();
    } catch (error) {
      console.error('Get interview error:', error);
      throw error;
    }
  }

  async startInterview(sessionId, jwtToken, sessionToken) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/interviews/${sessionId}/start`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to start interview');
      }

      return await response.json();
    } catch (error) {
      console.error('Start interview error:', error);
      throw error;
    }
  }

  async addInterviewMessage(sessionId, role, content, jwtToken, sessionToken, metadata = null) {
    try {
      const requestBody = {
        role,
        content,
        metadata
      };

      const response = await fetch(`${this.baseUrl}/api/v1/interviews/${sessionId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to add message');
      }

      return await response.json();
    } catch (error) {
      console.error('Add message error:', error);
      throw error;
    }
  }

  async completeInterview(sessionId, jwtToken, sessionToken) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/interviews/${sessionId}/complete`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to complete interview');
      }

      return await response.json();
    } catch (error) {
      console.error('Complete interview error:', error);
      throw error;
    }
  }

  async updateElapsedTime(sessionId, jwtToken, sessionToken) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/interviews/${sessionId}/elapsed-time`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update elapsed time');
      }

      return await response.json();
    } catch (error) {
      console.error('Update elapsed time error:', error);
      throw error;
    }
  }

  async analyzeInterview(sessionId, jwtToken, sessionToken, forceReAnalyze = false) {
    try {
      const requestBody = {
        forceReAnalyze,
        quickAnalysis: false
      };

      const response = await fetch(`${this.baseUrl}/api/v1/interviews/${sessionId}/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Interview analysis failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Interview analysis error:', error);
      throw error;
    }
  }

  async getInterviewAssessment(sessionId, jwtToken, sessionToken) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/interviews/${sessionId}/assessment`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch assessment');
      }

      return await response.json();
    } catch (error) {
      console.error('Get assessment error:', error);
      throw error;
    }
  }

  // Health Check
  async healthCheck() {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      if (!response.ok) {
        throw new Error('Health check failed');
      }
      return await response.json();
    } catch (error) {
      console.error('Health check error:', error);
      throw error;
    }
  }

  // Get available voices from unified backend
  async getAvailableVoices() {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/voices`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch voices');
      }

      const data = await response.json();

      // Handle nested response structure from ResponseInterceptor
      // Response can be: { status, message, data: { success, message, data: [...], defaultVoice } }
      // OR: { success, message, data: [...], defaultVoice }

      let voices = [];
      let defaultVoice = 'rachel';

      // Check if data is wrapped in an additional layer
      if (data.data && typeof data.data === 'object' && data.data.data) {
        // Nested structure - unwrap it
        voices = data.data.data || [];
        defaultVoice = data.data.defaultVoice || 'rachel';
      } else if (data.success && data.data) {
        // Direct structure
        voices = data.data || [];
        defaultVoice = data.defaultVoice || 'rachel';
      }

      return {
        voices: voices,
        defaultVoice: defaultVoice
      };
    } catch (error) {
      console.error('Get voices error:', error);
      // Return fallback voices if API call fails
      // Note: These are fallback voices with placeholder _id values
      // In production, voices should come from the database
      return {
        voices: [
          { _id: 'fallback-rachel', name: 'Rachel', description: 'Clear, professional female (American)' },
          { _id: 'fallback-adam', name: 'Adam', description: 'Deep, authoritative male (American)' },
          { _id: 'fallback-domi', name: 'Domi', description: 'Strong, assertive female (American)' },
          { _id: 'fallback-dave', name: 'Dave', description: 'Conversational male (British)' },
          { _id: 'fallback-antoni', name: 'Antoni', description: 'Well-articulated male (American)' },
          { _id: 'fallback-sarah', name: 'Sarah', description: 'Soft, clear female (American)' },
          { _id: 'fallback-thomas', name: 'Thomas', description: 'Calm, measured male (American)' },
          { _id: 'fallback-fin', name: 'Fin', description: 'Mature male (Irish)' },
          { _id: 'fallback-charlie', name: 'Charlie', description: 'Natural male (Australian)' },
          { _id: 'fallback-emily', name: 'Emily', description: 'Articulate female (American)' }
        ],
        defaultVoice: 'rachel'
      };
    }
  }

  // Fetch resumes from unified-backend
  async getMyResumes(jwtToken, sessionToken, options = {}) {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      // Add optional pagination params (defaults: page=1, limit=10)
      if (options.page) queryParams.append('page', options.page);
      if (options.limit) queryParams.append('limit', options.limit);

      // Add optional filter params
      if (options.roleIds) queryParams.append('roleIds', options.roleIds); // comma-separated
      if (options.completedStatus) queryParams.append('completedStatus', options.completedStatus); // comma-separated
      if (options.lastUpdated) queryParams.append('lastUpdated', options.lastUpdated); // last7days, last30days, older
      if (options.sortOrder) queryParams.append('sortOrder', options.sortOrder); // asc, desc

      const queryString = queryParams.toString();
      const url = `${this.baseUrl}/resume/my-resumes${queryString ? '?' + queryString : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch resumes');
      }

      const data = await response.json();

      // Response structure: { status, message, data: { resumes: [...], pagination: {...} } }
      return data.data?.resumes || data.resumes || [];
    } catch (error) {
      console.error('Get resumes error:', error);
      throw error;
    }
  }

  // Fetch job roles from unified-backend
  async getMyJobRoles(jwtToken, sessionToken) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/job-roles/user/roles`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch job roles');
      }

      const data = await response.json();

      // Response structure: { status, message, data: [...] }
      const jobRoles = data.data || data;
      return Array.isArray(jobRoles) ? jobRoles : [];
    } catch (error) {
      console.error('Get job roles error:', error);
      throw error;
    }
  }

  // Alias for getMyJobRoles to maintain compatibility
  async getJobRoles(jwtToken, sessionToken) {
    return this.getMyJobRoles(jwtToken, sessionToken);
  }

  // Fetch baseline tests for the current user
  async getBaselineTests(jwtToken, sessionToken) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/job-roles/baseline-test/user/tests`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch baseline tests');
      }

      const data = await response.json();

      // Response structure: { status, message, data: { success, message, data: { tests: [...] } } }
      // Navigate through the nested structure to get the tests array
      const baselineTests = data.data?.data?.tests || data.data?.tests || [];
      console.log('📊 Baseline tests fetched:', baselineTests);
      return Array.isArray(baselineTests) ? baselineTests : [];
    } catch (error) {
      console.error('Get baseline tests error:', error);
      throw error;
    }
  }

  // Sync resume data from unified-backend to voice server
  async syncResumeToVoiceServer(userEmail, resumeData, apiKey) {
    try {
      console.log('🔄 Syncing resume to voice server for:', userEmail);

      // Create a synthetic resume entry in voice server
      const formData = new FormData();
      formData.append('user_id', userEmail);

      // Create a simple text file from parsed resume data
      const resumeText = JSON.stringify(resumeData.parsedData || resumeData, null, 2);
      const blob = new Blob([resumeText], { type: 'text/plain' });
      const file = new File([blob], `${resumeData.fileName || 'resume.txt'}`, { type: 'text/plain' });
      formData.append('file', file);

      const response = await fetch(`${this.voiceServerUrl}/api/resume/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Resume sync failed');
      }

      console.log('✅ Resume synced to voice server');
      return await response.json();
    } catch (error) {
      console.error('❌ Resume sync error:', error);
      throw error;
    }
  }

  // Sync job role data from unified-backend to voice server as JD
  async syncJobRoleToVoiceServer(userEmail, jobRole, apiKey) {
    try {
      console.log('🔄 Syncing job role to voice server as JD for:', userEmail);

      const formData = new FormData();
      formData.append('user_id', userEmail);
      formData.append('job_title', jobRole.title || 'Job Role');
      formData.append('company_name', jobRole.companyName || 'Company');
      formData.append('jd_content', jobRole.description || 'No description provided');

      const response = await fetch(`${this.voiceServerUrl}/api/jd/upload-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'JD sync failed');
      }

      console.log('✅ Job role synced to voice server as JD');
      return await response.json();
    } catch (error) {
      console.error('❌ JD sync error:', error);
      throw error;
    }
  }

  // Get user's interviews (for feedback selection)
  async getMyInterviews(jwtToken, sessionToken, options = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.skip) queryParams.append('skip', options.skip);

      const queryString = queryParams.toString();
      const url = `${this.baseUrl}/api/v1/interviews${queryString ? '?' + queryString : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch interviews');
      }

      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Get my interviews error:', error);
      throw error;
    }
  }

  // Interview Feedback - Submit real interview feedback
  async submitInterviewFeedback(feedbackData, jwtToken, sessionToken) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/interview-feedback/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        },
        body: JSON.stringify(feedbackData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to submit interview feedback');
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Submit interview feedback error:', error);
      throw error;
    }
  }

  // Interview Feedback - Get user's submissions
  async getMyFeedbackSubmissions(jwtToken, sessionToken, page = 1, limit = 10) {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/v1/interview-feedback/my-submissions?page=${page}&limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${jwtToken}`,
            'x-session-token': sessionToken
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch feedback submissions');
      }

      const data = await response.json();
      // Handle double-wrapped response from interceptor
      return data?.data?.data || data?.data || data;
    } catch (error) {
      console.error('Get feedback submissions error:', error);
      throw error;
    }
  }

  // Interview Feedback - Get specific submission
  async getFeedbackSubmission(feedbackId, jwtToken, sessionToken) {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/v1/interview-feedback/submission/${feedbackId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${jwtToken}`,
            'x-session-token': sessionToken
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch feedback submission');
      }

      const data = await response.json();
      // Handle double-wrapped response from interceptor
      return data?.data?.data || data?.data || data;
    } catch (error) {
      console.error('Get feedback submission error:', error);
      throw error;
    }
  }

  // Interview Feedback - Get community questions
  async getCommunityQuestions(jwtToken, sessionToken, filters = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (filters.roleTag) queryParams.append('role_tag', filters.roleTag);
      if (filters.skillTags) queryParams.append('skill_tags', filters.skillTags);
      if (filters.difficultyLevel) queryParams.append('difficulty_level', filters.difficultyLevel);
      if (filters.questionType) queryParams.append('question_type', filters.questionType);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      const queryString = queryParams.toString();
      const url = `${this.baseUrl}/api/v1/interview-feedback/community-questions${queryString ? '?' + queryString : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch community questions');
      }

      const data = await response.json();
      // Handle double-wrapped response from interceptor
      return data?.data?.data || data?.data || data;
    } catch (error) {
      console.error('Get community questions error:', error);
      throw error;
    }
  }

  // Interview Feedback - Get available role tags
  async getRoleTags(jwtToken, sessionToken) {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/interview-feedback/role-tags`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'x-session-token': sessionToken
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch role tags');
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Get role tags error:', error);
      throw error;
    }
  }

  // Voice Bot Initialization (via Unified Backend)
  async initializeVoiceBot(interviewId, language = 'en-US', jwtToken = null, sessionToken = null, voiceId = null) {
    try {
      console.log('🎙️ Initializing voice bot via unified backend for interview:', interviewId, 'voiceId:', voiceId);

      // Validate required parameters
      if (!interviewId) {
        throw new Error('Interview ID is required. Please create an interview first.');
      }

      const requestBody = {
        language: language
      };

      // Only include voiceId if provided
      if (voiceId) {
        requestBody.voiceId = voiceId;
      }

      const response = await fetch(`${this.baseUrl}/api/v1/interviews/${interviewId}/initialize-bot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Send cookies automatically (access_token, session_id)
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Voice bot initialization failed:', response.status, errorText);
        let error;
        try {
          error = JSON.parse(errorText);
        } catch (e) {
          error = { message: errorText };
        }
        throw new Error(error.message || `Voice bot initialization failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Voice bot initialized successfully via unified backend');
      console.log('📦 Voice bot response data:', data);

      // Handle both wrapped and unwrapped response formats
      const responseData = data.data || data;

      return {
        sessionId: responseData.sessionId,
        roomName: responseData.roomName,
        livekitToken: responseData.livekitToken,
        livekitUrl: responseData.livekitUrl,
        expiresIn: responseData.expiresIn,
        userIdentity: responseData.userIdentity
      };
    } catch (error) {
      console.error('❌ Voice bot initialization error:', error);
      throw error;
    }
  }
}

export const apiService = new ApiService(); 